import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DollarSign, Receipt, CreditCard, TrendingUp } from "lucide-react";
import { format } from "date-fns";
import { StatCard, SectionHeader, ProgressRow } from "@/components/admin/AdminUI";
import { LoadingState, ErrorState, EmptyState } from "@/components/dashboard/common/PageShell";
import { useAdminOrders, useAdminProfiles } from "@/hooks/admin/useAdminMetrics";

const statusTone: Record<string, string> = {
  paid: "bg-emerald-600",
  pending: "bg-amber-600",
  failed: "bg-red-600",
  cancelled: "bg-muted text-foreground",
};

const PaymentsAndSubscriptions = () => {
  const { data: orders = [], isLoading, error, refetch } = useAdminOrders();
  const { data: profiles = [] } = useAdminProfiles();
  const [search, setSearch] = useState("");

  const totals = useMemo(() => {
    const paid = orders.filter((o) => o.status === "paid");
    const pending = orders.filter((o) => o.status === "pending");
    const gross = paid.reduce((s, o) => s + Number(o.total ?? 0), 0);
    const monthStart = new Date();
    monthStart.setDate(1);
    const mtd = paid
      .filter((o) => new Date(o.created_at) >= monthStart)
      .reduce((s, o) => s + Number(o.total ?? 0), 0);
    return {
      gross,
      mtd,
      paidCount: paid.length,
      pendingCount: pending.length,
      pendingValue: pending.reduce((s, o) => s + Number(o.total ?? 0), 0),
      avg: paid.length ? gross / paid.length : 0,
    };
  }, [orders]);

  const subs = useMemo(() => {
    const paidTiers = profiles.filter((p) => p.user_type === "creator" || p.user_type === "venue");
    const active = paidTiers.filter((p) => p.subscription_status === "active");
    const mrr =
      active.filter((p) => p.user_type === "creator").length * 249 +
      active.filter((p) => p.user_type === "venue").length * 849;
    return { paidTiers, active, mrr };
  }, [profiles]);

  const rows = useMemo(() => {
    const t = search.trim().toLowerCase();
    if (!t) return orders.slice(0, 100);
    return orders.filter((o) => [o.id, o.status].some((v) => v.toLowerCase().includes(t))).slice(0, 100);
  }, [orders, search]);

  if (isLoading) return <LoadingState label="Loading payments…" />;
  if (error) return <ErrorState message={(error as Error).message} onRetry={() => refetch()} />;

  return (
    <div className="space-y-8">
      <SectionHeader
        title="Payments & subscriptions"
        description="Settled revenue, outstanding checkouts and recurring subscription health."
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Gross settled"
          value={`R${totals.gross.toLocaleString("en-ZA", { maximumFractionDigits: 0 })}`}
          icon={DollarSign}
          tone="positive"
          hint={`${totals.paidCount} paid orders`}
        />
        <StatCard
          label="Month to date"
          value={`R${totals.mtd.toLocaleString("en-ZA", { maximumFractionDigits: 0 })}`}
          icon={TrendingUp}
        />
        <StatCard
          label="Awaiting payment"
          value={`R${totals.pendingValue.toLocaleString("en-ZA", { maximumFractionDigits: 0 })}`}
          icon={Receipt}
          tone={totals.pendingCount > 0 ? "warning" : "default"}
          hint={`${totals.pendingCount} open checkouts`}
        />
        <StatCard
          label="Estimated MRR"
          value={`R${subs.mrr.toLocaleString("en-ZA")}`}
          icon={CreditCard}
          hint={`${subs.active.length} active subscriptions`}
        />
      </div>

      <Tabs defaultValue="transactions" className="space-y-4">
        <TabsList>
          <TabsTrigger value="transactions">Transactions</TabsTrigger>
          <TabsTrigger value="subscriptions">Subscriptions</TabsTrigger>
        </TabsList>

        <TabsContent value="transactions" className="space-y-4">
          <Input
            placeholder="Search by order ID or status…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-md"
          />
          {rows.length === 0 ? (
            <EmptyState icon={Receipt} title="No transactions" description="Orders appear here as soon as checkouts start." />
          ) : (
            <div className="space-y-2">
              {rows.map((o) => (
                <Card key={o.id} className="bg-card border-border">
                  <CardContent className="flex flex-wrap items-center gap-4 p-4">
                    <span className="font-mono text-xs text-muted-foreground">{o.id.slice(0, 8)}</span>
                    <span className="flex-1 min-w-[120px] font-semibold text-foreground">
                      {o.currency} {Number(o.total ?? 0).toFixed(2)}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {format(new Date(o.created_at), "dd MMM yyyy HH:mm")}
                    </span>
                    <Badge className={`${statusTone[o.status] ?? "bg-muted"} border-0 capitalize`}>{o.status}</Badge>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="subscriptions">
          <div className="grid gap-6 lg:grid-cols-2">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-base text-foreground">Subscription coverage</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <ProgressRow
                  label="Creators on an active plan"
                  value={subs.active.filter((p) => p.user_type === "creator").length}
                  total={subs.paidTiers.filter((p) => p.user_type === "creator").length || 1}
                />
                <ProgressRow
                  label="Venues on an active plan"
                  value={subs.active.filter((p) => p.user_type === "venue").length}
                  total={subs.paidTiers.filter((p) => p.user_type === "venue").length || 1}
                />
                <p className="text-xs text-muted-foreground">
                  Creator plan R249/month · Venue plan R849/month. MRR is estimated from active paid-tier accounts.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-base text-foreground">Accounts needing follow-up</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {subs.paidTiers.filter((p) => p.subscription_status !== "active").slice(0, 8).map((p) => (
                  <div key={p.id} className="flex items-center justify-between rounded-lg bg-muted/50 px-3 py-2">
                    <span className="text-sm text-foreground">{p.full_name || p.username}</span>
                    <Badge variant="outline" className="capitalize">
                      {p.subscription_status ?? "inactive"}
                    </Badge>
                  </div>
                ))}
                {subs.paidTiers.every((p) => p.subscription_status === "active") && (
                  <p className="text-sm text-muted-foreground">All paid-tier accounts are current.</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PaymentsAndSubscriptions;
